export const diseaseModule = {
  id: "disease",
  title: "Kidney Diseases: Glomerular",
  sections: [
    {
      type: "text",
      title: "FSGS (Focal Segmental Glomerulosclerosis)",
      content: `
        <p><span class="glossary-term">FSGS</span> describes scarring affecting <em>some</em> (focal) glomeruli and only <em>part</em> of the affected glomerulus (segmental). It is the most common cause of nephrotic syndrome in adults in many countries.</p>

        <div class="highlight-box">
          <p><strong>Causes</strong></p>
          <ul>
            <li><strong>Primary/idiopathic</strong> — circulating permeability factor (possibly suPAR) damages podocytes</li>
            <li><strong>HIV</strong> — collapsing variant (most aggressive; collapse of the glomerular tuft)</li>
            <li><strong>Heroin use</strong></li>
            <li><strong>Obesity / hyperfiltration</strong> — adaptive FSGS; enlarged glomeruli</li>
            <li><strong>Reflux nephropathy</strong> — hyperfiltration in remaining nephrons</li>
            <li><strong>Drugs</strong> — interferon, pamidronate, calcineurin inhibitors</li>
            <li><strong>Sickle cell disease</strong> — chronic hyperfiltration</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Clinical features</strong></p>
          <ul>
            <li>Nephrotic syndrome (heavy proteinuria, hypoalbuminaemia, oedema, hyperlipidaemia)</li>
            <li>Hypertension and <span class="purple-term">renal impairment at presentation</span> (unlike MCD which is normotensive with normal GFR)</li>
            <li>Poor response to steroids (contrast with MCD which is steroid-responsive)</li>
            <li>Progresses to CKD and ESKD in ~50% over 10 years</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Treatment</strong></p>
          <ul>
            <li>ACEi/ARBs — reduce proteinuria and intraglomerular pressure</li>
            <li>Corticosteroids (high-dose prednisolone) — partial response in ~50%</li>
            <li>Calcineurin inhibitors (cyclosporin/tacrolimus) — steroid-resistant FSGS</li>
            <li><strong>High risk of recurrence in transplant</strong> (~30–50% for primary FSGS)</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">FSGS vs MCD</div>
          <div class="mnemonic-content"><strong>MCD</strong> — children, steroid-responsive, normotensive, normal GFR, charge loss only.<br/><strong>FSGS</strong> — adults, steroid-resistant, hypertension, renal impairment, structural podocyte damage.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 35-year-old HIV-positive patient presents with nephrotic syndrome and renal impairment. Biopsy shows collapse of the glomerular tuft with podocyte proliferation. What is the diagnosis?",
      options: [
        "Minimal change disease",
        "HIV-associated nephropathy (HIVAN) — collapsing FSGS",
        "Membranous nephropathy",
        "IgA nephropathy",
      ],
      correct: 1,
      explanation:
        "HIV-associated nephropathy (HIVAN) classically presents as the collapsing variant of FSGS — collapse of the glomerular capillary tuft with podocyte hypertrophy and proliferation. It is more common in patients of African descent and is characterised by rapid progression to ESKD. Antiretroviral therapy (ART) can slow progression. HIVAN is diagnosed clinically in an HIV-positive patient with nephrotic-range proteinuria.",
    },
    {
      type: "question",
      text: "Why is primary FSGS particularly problematic after renal transplantation?",
      options: [
        "Calcineurin inhibitors used for immunosuppression cause FSGS directly",
        "Primary FSGS recurs in the transplanted kidney due to a circulating permeability factor",
        "Transplant patients develop FSGS from chronic rejection",
        "FSGS always progresses to ESKD before transplantation is possible",
      ],
      correct: 1,
      explanation:
        "Primary FSGS is thought to be caused by a circulating permeability factor (possibly suPAR or CLCF1) that damages podocytes. This factor persists after transplantation and attacks the new kidney, causing recurrence in ~30–50% of cases. Recurrence can occur within hours of transplantation. Risk factors for recurrence include rapid original disease progression and young age. Plasmapheresis can be used to remove the factor after transplantation.",
    },
    {
      type: "text",
      title: "Membranous Nephropathy",
      content: `
        <p><span class="glossary-term">Membranous nephropathy</span> is the most common cause of nephrotic syndrome in non-diabetic white adults. It is characterised by <strong>subepithelial immune complex deposits</strong> that thicken the GBM.</p>

        <div class="highlight-box">
          <p><strong>Primary (idiopathic) membranous nephropathy</strong></p>
          <ul>
            <li><strong>PLA2R antibodies</strong> (~70%) — anti-phospholipase A2 receptor on podocytes</li>
            <li>THSD7A antibodies (~5%)</li>
            <li>Serum PLA2R titre correlates with disease activity and can guide treatment</li>
            <li>Histology: "spike and dome" pattern on silver stain (spikes = GBM projections between deposits)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Secondary causes — mnemonic "DISHA"</strong></p>
          <ul>
            <li><strong>D</strong>rugs — NSAIDs, gold, penicillamine, captopril</li>
            <li><strong>I</strong>nfections — hepatitis B (especially Asian patients), hepatitis C, syphilis, malaria</li>
            <li><strong>S</strong>ystemic disease — SLE (class V lupus nephritis)</li>
            <li><strong>H</strong>epatocarcinoma / solid tumours — lung, colon, breast (always exclude malignancy in adults >50)</li>
            <li><strong>A</strong>utoimmune — sarcoidosis, thyroiditis</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Natural history — "Rule of Thirds"</strong></p>
          <ul>
            <li>1/3 — spontaneous complete remission</li>
            <li>1/3 — persistent proteinuria but stable function</li>
            <li>1/3 — progressive CKD toward ESKD</li>
          </ul>
        </div>

        <p><strong>Thrombosis risk</strong>: Membranous nephropathy has the highest thrombosis risk of all nephrotic syndromes, particularly <span class="purple-term">renal vein thrombosis</span> → flank pain, haematuria, acute deterioration.</p>

        <p><strong>Treatment</strong>: ACEi/ARBs + conservative initially. If high-risk (rising creatinine, severe proteinuria, PLA2R titre rising): Ponticelli regimen (alternating steroids + chlorambucil) or <strong>rituximab</strong> (now preferred — anti-CD20, depletes B cells producing PLA2R antibodies).</p>
      `,
    },
    {
      type: "question",
      text: "A 55-year-old man presents with heavy proteinuria, hypoalbuminaemia, and leg oedema. PLA2R antibodies are positive. What is the most appropriate next investigation to exclude a secondary cause?",
      options: [
        "Renal biopsy — to confirm membranous pattern",
        "CT chest/abdomen/pelvis — to screen for occult malignancy",
        "Anti-dsDNA antibodies — to exclude lupus",
        "HbA1c — to exclude diabetic nephropathy",
      ],
      correct: 1,
      explanation:
        "Although PLA2R positivity strongly suggests primary membranous nephropathy, it is essential to screen for occult malignancy in adults over 50 with new-onset membranous nephropathy. Solid tumours (lung, colon, breast, gastric) can trigger secondary membranous nephropathy. PLA2R is usually negative in secondary disease, but a CT scan for malignancy is indicated regardless, as missing a treatable cancer is dangerous.",
    },
    {
      type: "question",
      text: "A patient with membranous nephropathy develops acute-onset flank pain and haematuria with worsening renal function. What complication should be suspected?",
      options: [
        "Acute interstitial nephritis from NSAIDs",
        "Renal vein thrombosis",
        "Rapidly progressive glomerulonephritis",
        "Urinary tract infection with pyelonephritis",
      ],
      correct: 1,
      explanation:
        "Membranous nephropathy has the highest risk of renal vein thrombosis among the nephrotic syndromes, due to urinary loss of antithrombin III, protein C, and protein S (all small enough to cross the leaky glomerulus). Renal vein thrombosis presents with flank pain, gross haematuria, sudden increase in proteinuria, and acute decline in renal function. Diagnosis is confirmed with CT venography or MRI. Treatment: anticoagulation.",
    },
    {
      type: "text",
      title: "Diabetic Nephropathy",
      content: `
        <p><span class="glossary-term">Diabetic nephropathy</span> is the leading cause of end-stage kidney disease worldwide. It develops in ~30–40% of people with type 1 DM and ~20–30% with type 2 DM.</p>

        <div class="highlight-box">
          <p><strong>Pathogenesis</strong></p>
          <ul>
            <li>Hyperglycaemia → <strong>advanced glycation end products (AGEs)</strong> → mesangial expansion</li>
            <li>Haemodynamic injury: hyperfiltration → ↑intraglomerular pressure → GBM thickening</li>
            <li>RAAS activation → angiotensin II → efferent constriction → further hyperfiltration</li>
            <li>Oxidative stress and VEGF upregulation</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Clinical stages (Mogensen classification)</strong></p>
          <ul>
            <li><strong>Stage 1</strong>: Hyperfiltration (↑GFR), renal hypertrophy; reversible with glucose control</li>
            <li><strong>Stage 2</strong>: Silent structural changes (GBM thickening, mesangial expansion); normal albumin</li>
            <li><strong>Stage 3</strong>: Microalbuminuria (30–300 mg/day, ACR 3–30 mg/mmol) — earliest clinical sign</li>
            <li><strong>Stage 4</strong>: Macroalbuminuria / overt proteinuria (>300 mg/day); ↑BP; GFR declining</li>
            <li><strong>Stage 5</strong>: ESKD (GFR &lt;15) — dialysis or transplant</li>
          </ul>
        </div>

        <p><strong>Histological hallmark</strong>: <span class="purple-term">Kimmelstiel-Wilson nodules</span> — ovoid nodules of mesangial matrix in glomeruli (pathognomonic). Also: diffuse mesangial expansion, GBM thickening, hyaline arteriolosclerosis, efferent > afferent arteriole (reverse of other causes).</p>

        <p><strong>Clinical tip</strong>: If a diabetic patient has nephropathy but <em>no retinopathy</em>, consider an alternative diagnosis — true diabetic nephropathy almost always co-exists with retinopathy.</p>

        <div class="highlight-box">
          <p><strong>Treatment</strong></p>
          <ul>
            <li>Glycaemic control (HbA1c target ~53 mmol/mol / 7%)</li>
            <li><strong>ACEi/ARBs</strong> — reduce proteinuria and intraglomerular pressure (first-line)</li>
            <li><strong>SGLT2 inhibitors</strong> (e.g., empagliflozin, dapagliflozin) — renoprotective independently of glucose</li>
            <li><strong>Finerenone</strong> (non-steroidal MR antagonist) — reduces CKD progression in diabetic nephropathy</li>
            <li>BP target &lt;130/80 mmHg; lipid control</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A type 2 diabetic patient has proteinuria but no diabetic retinopathy. What does this suggest?",
      options: [
        "Early diabetic nephropathy before retinopathy has developed",
        "An alternative renal diagnosis — true diabetic nephropathy almost always co-exists with retinopathy",
        "The patient has type 1 DM, not type 2",
        "The proteinuria is from orthostatic causes",
      ],
      correct: 1,
      explanation:
        "Diabetic nephropathy almost always co-exists with diabetic retinopathy (both share the same microvascular pathology). If a diabetic patient has significant proteinuria or CKD but no retinopathy, an alternative cause of nephropathy should be sought (e.g., IgA nephropathy, membranous nephropathy, FSGS) and renal biopsy should be considered rather than assuming diabetic nephropathy.",
    },
    {
      type: "question",
      text: "Which histological finding is pathognomonic of diabetic nephropathy?",
      options: [
        "Wire loop lesions in the glomerulus",
        "Kimmelstiel-Wilson nodules (ovoid mesangial matrix deposits)",
        "Subepithelial spike and dome deposits on silver stain",
        "Linear IgG along the GBM",
      ],
      correct: 1,
      explanation:
        "Kimmelstiel-Wilson nodules are pathognomonic of diabetic nephropathy — they are ovoid nodules of acellular mesangial matrix deposited in a nodular pattern within the glomerulus. Other histological features include diffuse mesangial expansion and GBM thickening, but nodular glomerulosclerosis (Kimmelstiel-Wilson) is diagnostic.",
    },
    {
      type: "text",
      title: "Renal Amyloidosis",
      content: `
        <p><span class="glossary-term">Amyloidosis</span> results from extracellular deposition of misfolded protein fibrils in organs. The kidney is the most commonly affected organ in systemic amyloidosis, causing <strong>nephrotic syndrome</strong>.</p>

        <div class="highlight-box">
          <p><strong>Types relevant to kidney disease</strong></p>
          <ul>
            <li><strong>AL amyloidosis</strong> — immunoglobulin light chains (lambda &gt; kappa); from plasma cell dyscrasia: multiple myeloma, MGUS, Waldenström's macroglobulinaemia; most common type in the West</li>
            <li><strong>AA amyloidosis</strong> — serum amyloid A protein; secondary to chronic inflammation: rheumatoid arthritis, Crohn's disease, bronchiectasis, chronic osteomyelitis, FMF (familial Mediterranean fever)</li>
            <li><strong>ATTR</strong> — transthyretin; hereditary (TTR mutations) or wild-type/senile (elderly men, cardiomyopathy)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Renal features</strong></p>
          <ul>
            <li>Nephrotic-range proteinuria (massive; &gt;10 g/day possible)</li>
            <li>Progressive CKD</li>
            <li>Renomegaly (enlarged, echogenic kidneys)</li>
            <li>Autonomic neuropathy, orthostatic hypotension (amyloid deposits in vessels)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Diagnosis</strong></p>
          <ul>
            <li><span class="purple-term">Congo red stain</span> → apple-green birefringence under polarised light</li>
            <li>Electron microscopy: randomly arranged non-branching fibrils (~10 nm)</li>
            <li>Fat pad aspirate or rectal biopsy (safer than kidney biopsy, but kidney gives highest yield)</li>
            <li>Serum/urine protein electrophoresis + immunofixation (for AL)</li>
            <li>SAA level (for AA)</li>
          </ul>
        </div>

        <p><strong>Treatment</strong>: AL — bortezomib/lenalidomide + dexamethasone; autologous stem cell transplantation (selected patients). AA — treat the underlying inflammatory condition; IL-6 inhibitors (tocilizumab), colchicine (FMF). ATTR — tafamidis (stabilises transthyretin tetramer).</p>
      `,
    },
    {
      type: "question",
      text: "A patient with long-standing rheumatoid arthritis develops heavy proteinuria and CKD. Renal biopsy with Congo red stain shows apple-green birefringence under polarised light. What type of amyloidosis is this and what is the precursor protein?",
      options: [
        "AL amyloidosis — immunoglobulin light chains",
        "AA amyloidosis — serum amyloid A protein",
        "ATTR amyloidosis — transthyretin",
        "Beta-2 microglobulin amyloidosis — from dialysis",
      ],
      correct: 1,
      explanation:
        "This is AA amyloidosis. In chronic inflammatory conditions such as rheumatoid arthritis, serum amyloid A (SAA) — an acute-phase reactant produced by the liver — is chronically elevated. Over years, SAA protein misfolds and deposits as amyloid fibrils in organs, particularly the kidney (nephrotic syndrome). Congo red staining with apple-green birefringence under polarised light is the diagnostic hallmark of all amyloid types.",
    },
    {
      type: "text",
      title: "Lupus Nephritis",
      content: `
        <p><span class="glossary-term">Lupus nephritis</span> occurs in 60–80% of SLE patients and is the leading cause of morbidity and mortality in SLE. It results from glomerular deposition of immune complexes containing anti-dsDNA and complement.</p>

        <div class="highlight-box">
          <p><strong>ISN/RPS Classification</strong></p>
          <ul>
            <li><strong>Class I</strong>: Minimal mesangial — normal on LM; mesangial deposits on IF/EM</li>
            <li><strong>Class II</strong>: Mesangial proliferative — mesangial deposits + hypercellularity</li>
            <li><strong>Class III</strong>: Focal proliferative — &lt;50% glomeruli affected; segmental lesions</li>
            <li><strong>Class IV</strong>: Diffuse proliferative — &gt;50% glomeruli; most severe and most common</li>
            <li><strong>Class V</strong>: Membranous lupus — subepithelial deposits; nephrotic features</li>
            <li><strong>Class VI</strong>: Advanced sclerosis — &gt;90% glomeruli sclerosed; no active inflammation</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Key features</strong></p>
          <ul>
            <li><strong>Histology (Class IV)</strong>: "Wire loop" lesions — subendothelial immune complex deposits create a thickened, rigid capillary wall appearance</li>
            <li><strong>Immunofluorescence</strong>: <span class="purple-term">"Full house"</span> — IgG, IgM, IgA, C3, C4, C1q all positive (unique to lupus)</li>
            <li><strong>Complement</strong>: ↓C3 AND ↓C4 (classical pathway activated via C1q binding immune complexes)</li>
            <li><strong>Anti-dsDNA antibodies</strong>: titre correlates with disease activity — rises before flares</li>
          </ul>
        </div>

        <p><strong>Treatment</strong>: Hydroxychloroquine (all SLE; reduces flares, prolongs survival). Class III/IV/V: steroids + mycophenolate mofetil (MMF) — first-line. Severe Class IV: cyclophosphamide (Euro-Lupus regimen). Newer agents: belimumab (anti-BAFF), voclosporin (calcineurin inhibitor), anifrolumab (anti-type I IFN).</p>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Complement in GN</div>
          <div class="mnemonic-content"><strong>↓C3 only</strong> — Alternative pathway (PSGN, MPGN type II, aHUS)<br/><strong>↓C3 + ↓C4</strong> — Classical pathway (Lupus, cryoglobulinaemia, PSGN occasionally)<br/><strong>Normal C3 + C4</strong> — IgA nephropathy, anti-GBM, ANCA vasculitis</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "Immunofluorescence of a renal biopsy shows 'full house' staining — IgG, IgM, IgA, C3, C4, and C1q all positive. Which diagnosis does this pattern strongly suggest?",
      options: [
        "Post-streptococcal GN",
        "IgA nephropathy",
        "Lupus nephritis",
        "Anti-GBM disease (Goodpasture)",
      ],
      correct: 2,
      explanation:
        "The 'full house' immunofluorescence pattern (IgG, IgM, IgA + C3, C4, C1q all positive) is virtually pathognomonic of lupus nephritis. This occurs because SLE produces immune complexes containing multiple antibody classes, which activate the classical complement pathway (explaining ↓C3 AND ↓C4). Anti-GBM disease shows linear IgG; IgA nephropathy shows predominantly IgA deposits; PSGN shows granular C3.",
    },
    {
      type: "question",
      text: "A patient with known SLE has a rise in anti-dsDNA titres and a fall in complement levels at routine clinic. Her creatinine has risen from 80 to 110 µmol/L. What does this pattern suggest and what should be done?",
      options: [
        "Stable disease — this fluctuation is normal in SLE",
        "Renal flare of lupus nephritis — renal biopsy and intensification of treatment should be considered",
        "Infection — antibiotics should be started",
        "Drug-induced nephritis from hydroxychloroquine",
      ],
      correct: 1,
      explanation:
        "Rising anti-dsDNA antibodies + falling complement (C3/C4) + rising creatinine is the classic triad of a lupus nephritis flare. Anti-dsDNA titre correlates directly with disease activity and typically rises before clinical deterioration. This combination warrants renal biopsy to classify the nephritis (class will guide treatment intensity) and likely intensification of immunosuppression (e.g., increasing steroids, adding MMF or cyclophosphamide).",
    },
    {
      type: "text",
      title: "Rapidly Progressive GN & ANCA Vasculitis",
      content: `
        <p><span class="glossary-term">Rapidly progressive GN (RPGN)</span>, or crescentic GN, is defined by &gt;50% crescents on biopsy. It causes a dramatic loss of GFR over days to weeks and is a renal emergency.</p>

        <div class="highlight-box">
          <p><strong>Three types by immunofluorescence</strong></p>
          <ul>
            <li><strong>Type I — Linear IgG</strong>: Anti-GBM disease (Goodpasture syndrome); anti-GBM antibodies against type IV collagen α3 chain; pulmonary haemorrhage + RPGN</li>
            <li><strong>Type II — Granular IF</strong>: Immune complex GN; causes: lupus, PSGN, IgA nephropathy, cryoglobulinaemia, MPGN</li>
            <li><strong>Type III — Pauci-immune (no/minimal IF)</strong>: ANCA-associated vasculitis; GPA, MPA, EGPA</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>ANCA-associated vasculitis (AAV)</strong></p>
          <ul>
            <li><strong>GPA</strong> (granulomatosis with polyangiitis — formerly Wegener's): <span class="purple-term">PR3-ANCA (c-ANCA)</span>
              <br/>Upper respiratory: saddle-nose deformity, bloody nasal discharge, sinusitis, oral ulcers
              <br/>Lower respiratory: pulmonary nodules/cavities ("coin lesions"), haemoptysis
              <br/>Renal: pauci-immune GN</li>
            <li><strong>MPA</strong> (microscopic polyangiitis): <span class="purple-term">MPO-ANCA (p-ANCA)</span>
              <br/>Pulmonary capillaritis (haemoptysis) + pauci-immune GN; no granulomas; no upper airway</li>
            <li><strong>EGPA</strong> (eosinophilic GPA — Churg-Strauss): MPO-ANCA (p-ANCA)
              <br/><strong>Asthma</strong> + peripheral <strong>eosinophilia</strong> + granulomatous inflammation + GN; mononeuritis multiplex</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Treatment of RPGN</strong></p>
          <ul>
            <li>Methylprednisolone IV pulses + <strong>cyclophosphamide</strong> (or rituximab for AAV)</li>
            <li><strong>Plasmapheresis</strong> — for anti-GBM disease (remove antibodies) or severe pulmonary haemorrhage</li>
            <li>Renal replacement therapy if oliguric ESKD</li>
            <li>Maintenance: azathioprine or rituximab; relapse monitoring with ANCA titres</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">ANCA Mnemonic</div>
          <div class="mnemonic-content"><strong>"PR3 Goes on Cruise control" = GPA → c-ANCA (PR3)</strong><br/><strong>"MPO Makes the Periphery pulse" = MPA/EGPA → p-ANCA (MPO)</strong></div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient presents with haemoptysis, saddle-nose deformity, bloody nasal discharge, and rapidly progressive renal failure. ANCA testing shows c-ANCA positivity. What is the diagnosis?",
      options: [
        "Microscopic polyangiitis (MPA) — MPO-ANCA",
        "Granulomatosis with polyangiitis (GPA, Wegener's) — PR3-ANCA (c-ANCA)",
        "Goodpasture syndrome — anti-GBM antibodies",
        "Eosinophilic GPA (Churg-Strauss) — asthma + eosinophilia",
      ],
      correct: 1,
      explanation:
        "GPA (formerly Wegener's granulomatosis) classically involves upper respiratory tract (saddle-nose deformity from cartilage destruction, bloody nasal discharge, sinusitis), lower respiratory tract (pulmonary nodules/cavities), and pauci-immune GN. PR3-ANCA (c-ANCA pattern) is highly specific. Granulomas are present. Goodpasture syndrome would show anti-GBM antibodies and linear IgG on IF, not c-ANCA.",
    },
    {
      type: "question",
      text: "A patient with ANCA vasculitis is found to have both haemoptysis and rapidly progressive GN. Which additional treatment should be added to steroids + cyclophosphamide?",
      options: [
        "Rituximab to deplete B cells",
        "Plasmapheresis to remove ANCA antibodies and prevent further pulmonary/renal damage",
        "Azathioprine for maintenance immunosuppression",
        "Eculizumab to block complement activation",
      ],
      correct: 1,
      explanation:
        "Plasmapheresis (plasma exchange) is indicated in ANCA-associated vasculitis when there is severe pulmonary haemorrhage or severe renal failure (creatinine >500 µmol/L or dialysis-dependent). It rapidly removes circulating ANCA antibodies and inflammatory mediators. For anti-GBM disease, plasmapheresis is always indicated regardless of severity. Azathioprine is used for maintenance (not induction). Rituximab can replace cyclophosphamide in induction but is not an indication for plasmapheresis.",
    },
    {
      type: "question",
      text: "Which type of RPGN shows pauci-immune (no or minimal) immunofluorescence staining on biopsy?",
      options: [
        "Type I — anti-GBM disease (linear IgG)",
        "Type II — immune complex GN (granular IF)",
        "Type III — ANCA-associated vasculitis",
        "Type IV — complement-mediated GN",
      ],
      correct: 2,
      explanation:
        "Type III RPGN (ANCA-associated vasculitis — GPA, MPA, EGPA) is characterised by pauci-immune crescentic GN — few or no immune deposits are visible on immunofluorescence, despite active inflammatory GN. The inflammation is driven by ANCA-mediated neutrophil activation rather than immune complex deposition. This contrasts with Type I (linear IgG = anti-GBM) and Type II (granular IF = immune complexes).",
    },
  ],
};
